// Copyright 2017 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { Disposable } from "aurelia-framework";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { GoogleAnalytics } from "libraries/GoogleAnalytics/GoogleAnalytics";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    IWorkflowModuleConfig,
    WorkflowModuleTypes,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { ScopedWorkflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/ModuleSetScopeObserver";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { RecommendationEngine, RecommendedAnalysis } from "services/RecommendationEngine/RecommendationEngine";
import tippy from "tippy.js";

interface AnalysisGroup {
    analysisTypes: IWorkflowModuleConfig[];
}

@autoinject
export class AnalysisModal implements IModal<AnalysisModalState, AnalysisModalReturnState> {
    private modalInstance: IWorkflowModuleModalInstance<AnalysisModalState, AnalysisModalReturnState>;
    private state: AnalysisModalState;
    private returnState: IWorkflowModuleConfig;
    private frequencySubscription: Disposable;

    private analysisGroups: AnalysisGroup[] = [];

    private recommendedNextStep: RecommendedAnalysis;

    private recommendationEngine: RecommendationEngine;

    constructor(
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {}

    attached() {
        this.observeDOMChanges();
    }

    detached() {
        if (this.frequencySubscription) {
            this.frequencySubscription.dispose();
        }
    }

    public async activateModal(
        state: AnalysisModalState,
        modalInstance: IWorkflowModuleModalInstance<AnalysisModalState, AnalysisModalReturnState>
    ): Promise<void> {
        this.state = state;
        this.modalInstance = modalInstance;

        this.recommendationEngine = new RecommendationEngine(this.processorResolver)
            .forMeasurement(this.state.measurement)
            .withModules(modalInstance.parentModule.getWorkflow().getModules());

        const analysisTypes = await this.recommendationEngine.getAnalysisTypes();

        this.frequencySubscription = this.frequencySelectionService.subscribeToRangeChanges(
            this.updateRecommendedNextStep.bind(this)
        );
        this.updateRecommendedNextStep();

        // Set all analysis types disabled if there is no measurement
        if (!state.measurement) {
            for (const analysisType of analysisTypes) {
                analysisType.isDisabled = true;
                analysisType.disabledReason = "Select a measurement first";
            }
        }

        // Group the analysis types by type
        for (const type of WorkflowModuleTypes.AllAnalysis) {
            this.analysisGroups.push({ analysisTypes: analysisTypes.filter((x) => x.moduleType == type) });
        }
    }

    private updateRecommendedNextStep() {
        const hasFrequencySelected = !(
            !this.frequencySelectionService.selectedRanges || this.frequencySelectionService.selectedRanges.length == 0
        );
        if (this.state.measurement) {
            this.recommendedNextStep = this.recommendationEngine.getRecommendedNextAnalysisStep(hasFrequencySelected);
        }
    }

    /**
     * Start observing DOM Changes so that we can link the tooltips
     */
    private observeDOMChanges() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" || mutation.type === "attributes") {
                    this.linkTooltips();
                }
            });
        });

        // Observe any tooltips that are added to this modal
        const targetNode = document.getElementById("analysis-type-modal");
        observer.observe(targetNode, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["tooltip"],
        });
    }

    private linkTooltips() {
        const elements = document.querySelectorAll("[tooltip]");

        tippy(elements, {
            content(reference) {
                return reference.getAttribute("tooltip");
            },
            theme: "sorama",
        });
    }

    public close(): AnalysisModalReturnState {
        return <AnalysisModalReturnState>{
            selectedAnalysisTypeId: this.returnState,
        };
    }

    public select(config: IWorkflowModuleConfig) {
        GoogleAnalytics.trackEvent("Open_Analysis_Module", {
            button_name: config.name,
            location: "Analysis modal button group",
        });

        // If disabled, do nothing
        if (config.isDisabled) {
            return;
        }

        this.modalInstance.parentModule
            .getWorkflow()
            .addModule(config)
            .then((newModule) => {
                this.state.selectedMeasurementIds.add(newModule.moduleId, [this.state.recordingId]);
            });
    }
}

// View-Model for modal needs to be first class in the file
export class AnalysisModalState {
    /**
     * The analysis type modal will determine the recommended/available types, based on this measurement.
     */
    public measurement?: GenericMeasurement;

    public selectedMeasurementIds: ScopedWorkflowProperty<string[]>;

    public recordingId: string;
}

export class AnalysisModalReturnState {
    public selectedAnalysisTypeId: IWorkflowModuleConfig;
}
